import { useState, useEffect, useCallback, useMemo } from 'react';

import { isLargeDesktop, isXsMobile } from '@zola-helpers/client/dist/es/util/responsive';
import { VendorCardBadgeView } from '@zola/svc-marketplace-ts-types';
import { ProductClicked } from '@zola/tracking-contracts/src/events';
import StarRatings from '@zola/zola-ui/src/components/StarRatings/StarRatings';
import { TagV2Size, TagV2Variant } from '@zola/zola-ui/src/components/Tag/TagV2';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import { useEffectOnce } from '@zola/zola-ui/src/hooks';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';

import cx from 'classnames';
import _uniq from 'lodash/uniq';
import LazyLoad from 'react-lazyload';

import BestOfZolaPng from '~/assets/images/badges/best_of_zola.png';
import CardLinkWrapper from '~/components/common/cards/components/CardLinkWrapper';
import {
  getDisplayPrice,
  getVendorsPerPrice,
  showPerEntityPricing,
} from '~/components/common/cards/util/helper';
import Carousel from '~/components/common/zolaUI/Carousel/Carousel';
import { doTrackVendorCardClickAndView } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { useUserContext } from '~/contexts/UserContext';
import { filterSupportedAwards } from '~/meta/awards';
import { CAROUSEL_SETTINGS } from '~/util/carouselSettings';
import EXPERIMENT_FLAGS, { SRP_VENDOR_PRICING_RATING } from '~/util/experimentFlags';
import { formatLocation } from '~/util/formatters';
import { formatRating } from '~/util/reviewUtils';
import { getCanonicalUrl } from '~/util/storefrontUrl';
import { TrackingProductLocation } from '~/util/trackingHelper';
import { getVendorIconV3 } from '~/util/vendorUtils';

import { ResponsiveVendorTagV2 } from '../../badges/ResponsiveVendorTagV2';
import { StorefrontFavoriteButton } from '../../favorites/FavoriteButton';
import CachedImage from '../../images/CachedImage';
import { useShowPostFavoriteOptIn } from '../../modals/InvitesFromVendorsOptIn/showPostFavoriteOptIn';
import { VendorCardVendor } from '../util/types';
import { AvailabilityTag } from './components/AvailabilityTag';
import BadgeList from './components/BadgeList';
import { CardDetails } from './components/CardDetails';
import VendorTypeAndLocation from './components/VendorTypeAndLocation';

import styles from './vendorCard.module.less';

export type VendorCardProps = {
  /**
   * Result of vendorCardViewToVendorCardVendor or storefrontCardViewToVendorCardVendor mapper
   *
   * Please do not change this prop to accept any other types; instead create a new mapper
   * that outputs this type, or update the existing mappers
   */
  vendor: VendorCardVendor;
  availabilityDates?: string[] | null | string;
  /** 1 based position of the card for tracking */
  position: number;
  onClick?: ((event: React.MouseEvent<HTMLElement>) => void) | null;
  scrollContainer?: string | Element;

  /**
   * Show the Best of Zola award for award recipients
   *
   * Default: false
   */
  showAward?: boolean;

  /** single badge in upper left overlaying image */
  showOverlayBadge?: boolean;
  /** list of badges below card details */
  showBadgeList?: boolean;
  /**
   * Optional tag color variant to use for the badges.  If we need different color
   * tags, we can add a content slot for the tags (badgeList?: React.ReactNode).
   */
  badgeColorVariant?: TagV2Variant;

  /**
   * Show where the vendor is based
   *
   * Default: true
   */
  showLocation?: boolean;

  /**
   * Show if the vendor services the "market".  When set, this takes the
   * metroType from the VendorCardVendor and the queriedLocation and combines
   * them to show "Based in <queried location>", "Serves <queried location>", or
   * "Serves <queried location> for a fee" (text may vary).
   *
   * Default: true
   */
  showServiceLocation?: boolean;
  /**
   * Instead of showing _nothing_ if the vendor is based in the queriedLocation,
   * show "Based in <queried location>" where we would show the other service
   * info.
   *
   * Defaults to false
   */
  showHomeService?: boolean;

  /**
   * A string, probably City, StateCode, indicating where the couple is currently
   * searching, or possibly their wedding location (LP, CLP).  When showServiceLocation
   * is set, this location is displayed when the vendor is based in that location,
   * services that location for free, or services that location for a fee.  That
   * "service" determination comes from the VendorCardVendor's metroType field.
   */
  queriedLocation?: string;

  /**
   * Show the favorite/unfavorite button.
   *
   * Default: true
   */
  showFavorites?: boolean;
  /**
   * Show the storefronts starting price, if we have it, and the storefront has
   * has opted to show price.
   *
   * Default: true
   */
  showPrice?: boolean;
  /**
   * Show review rating summaries if the storefront has reviews.
   *
   * Default: true
   */
  showRatings?: boolean;

  /**
   * For some vendor categories, show additional details if present in the vendor
   * card vendor.  For example: show facet information (team size, indoor/outdoor,
   * service level, etc).
   *
   * Default: true
   */
  showDetails?: boolean;

  /**
   * Show the vendor category.  Most useful when mixed cards are shown in the same
   * section.
   *
   * Default: true
   */
  showVendorType?: boolean;

  readOnly?: boolean;
  searchLocation?: string | null;

  /** Show the carousel: default: true. */
  showCarousel?: boolean;
  carouselPhotoIds?: string[];
  /** Where is this card being displayed?  Used for tracking favorite clicks */
  location?: TrackingProductLocation;

  /** Tracking Prop  */
  listingShelfName?: ProductClicked['listing_shelf_name'];
  trackClick: boolean;

  lazyload?: boolean;
  className?: string;

  /** Scale the card size when hovered, default true. */
  scaleOnHover?: boolean;
  /** self-contained image scale, where containing div does not expand, img only "zooms in" */
  altScaleOnHover?: boolean;

  /**
   * Vendor cards have a 20px bottom margin on them.  That should not be part
   * of the component: margin should be specified where the card is used.  However
   * to just globally sweep away the margin is maybe too much to bite off at
   * once, so this property is here to migrate to no-margin cards.  When noMargin
   * is false everywhere, we can remove the prop.  Deal?
   */
  noMargin?: boolean;

  aspectRatio?: '4:3' | '7:5';

  fromSrp?: boolean;
  isPricingRatingTest1?: boolean;
  isPricingRatingTest2?: boolean;
};

export const VendorCard = (props: VendorCardProps): JSX.Element => {
  const {
    vendor,
    searchLocation = null,
    onClick,
    availabilityDates,
    position,
    location,
    scrollContainer,
    showAward = false,
    showOverlayBadge = true,
    showBadgeList = false,
    showLocation = true,
    showServiceLocation = true,
    showHomeService = false,
    queriedLocation,
    showFavorites = true,
    showDetails = true,
    showVendorType = true,
    showRatings = true,
    showPrice = true,
    readOnly = false,
    showCarousel = true,
    carouselPhotoIds,
    lazyload = true,
    badgeColorVariant,
    className,
    scaleOnHover = true,
    altScaleOnHover,
    aspectRatio,
    noMargin = false,
    listingShelfName,
    trackClick,
    fromSrp,
    isPricingRatingTest1,
    isPricingRatingTest2,
  } = props;
  const {
    awards,
    storefrontUuid,
    priceRangeVisible,
    priceTier,
    vendorName,
    taxonomyNodeKey,
    imageIds,
    storefrontSlug,
    details,
    metroType,
    reviewsCount,
    badges,
    price,
    searchScore,
    displayAvailability,
    availableDates: vendorAvailableDates,
    storefrontCoverId,
  } = vendor;
  const to =
    storefrontSlug && taxonomyNodeKey && !readOnly
      ? getCanonicalUrl(taxonomyNodeKey, storefrontSlug)
      : null;
  const displayAward = showAward && filterSupportedAwards(awards).length > 0;
  const locationString =
    vendor.city && vendor.stateProvince
      ? `${vendor.city}, ${vendor.stateProvince}`
      : searchLocation;
  const displayLocation = showLocation ? locationString : null;
  const hasNoMinumumPrice = price?.min === null || price?.min === undefined;
  const whichDisplayPrice = showPerEntityPricing(taxonomyNodeKey, price)
    ? getVendorsPerPrice(taxonomyNodeKey, details)
    : getDisplayPrice(price?.min);
  const displayPrice = showPrice && priceRangeVisible ? whichDisplayPrice : null;

  const [carouselCards, setCarouselCards] = useState<JSX.Element[]>([]);
  const [showArrowButtons, setShowArrowButtons] = useState(false);

  const showRatingStars =
    showRatings && Boolean(vendor.averageReviewsRate && reviewsCount && reviewsCount > 0);

  const isUnderPricingRatingTest = useMemo(
    () => isPricingRatingTest1 || isPricingRatingTest2,
    [isPricingRatingTest1, isPricingRatingTest2]
  );
  const userContext = useUserContext();

  useEffectOnce(() => {
    // Trigger experiment viewed event only on SRP
    if (fromSrp) {
      EXPERIMENT_FLAGS.getExperimentVariation(SRP_VENDOR_PRICING_RATING, userContext);
    }
  });

  const getCarouselImages = useCallback(() => {
    if (imageIds?.length) {
      return imageIds;
    }

    if (carouselPhotoIds?.length) {
      return _uniq([storefrontCoverId, ...carouselPhotoIds]).filter(Boolean) as string[];
    }

    if (storefrontCoverId) {
      return [storefrontCoverId];
    }

    return [];
  }, [carouselPhotoIds, imageIds, storefrontCoverId]);

  useEffect(() => {
    if (getCarouselImages().length) {
      setCarouselCards(
        getCarouselImages()
          .slice(0, 5)
          .map((imageId: string, idx: number) => {
            return (
              <div
                className={cx(styles.carouselPhoto, {
                  [styles.sevenToFive]: aspectRatio === '7:5',
                })}
                id={`photo-${idx}`}
                key={imageId}
              >
                <CachedImage
                  uuid={imageId}
                  srcSizes={[
                    { width: 200, height: 150 },
                    { width: 250, height: 188 },
                    { width: 300, height: 225 },
                    { width: 400, height: 300 },
                    { width: 500, height: 375 },
                    { width: 600, height: 450 },
                  ]}
                  fit="crop"
                  quality={60}
                  loading={lazyload || idx > 0 ? 'lazy' : 'eager'}
                />
              </div>
            );
          })
      );
    }
  }, [aspectRatio, getCarouselImages, lazyload]);

  const coverPhotoDisplay = useMemo(
    () => (
      <div className={cx(styles.carouselPhoto, { [styles.sevenToFive]: aspectRatio === '7:5' })}>
        <div>
          {getCarouselImages().length ? (
            <CachedImage
              uuid={getCarouselImages()[0]}
              srcSizes={[
                { width: 200, height: 150 },
                { width: 250, height: 188 },
                { width: 300, height: 225 },
                { width: 400, height: 300 },
                { width: 500, height: 375 },
                { width: 600, height: 450 },
              ]}
              fit="crop"
              quality={60}
              loading={lazyload ? 'lazy' : 'eager'}
            />
          ) : (
            <div className={styles.icon}>{getVendorIconV3(taxonomyNodeKey || 'all')({})}</div>
          )}
        </div>
      </div>
    ),
    [aspectRatio, getCarouselImages, lazyload, taxonomyNodeKey]
  );

  const doTrackClick = useCallback(
    (includeTags: boolean) => {
      doTrackVendorCardClickAndView({
        vendor,
        position,
        listingShelfName,
        includeTags,
      });
    },
    [position, vendor, listingShelfName]
  );

  const getCoverImage = useCallback(() => {
    if (showCarousel && carouselCards.length > 1 && !readOnly) {
      return (
        <Carousel
          content={carouselCards}
          settings={CAROUSEL_SETTINGS}
          useV2ArrowStyles
          useSmallerV2Arrows
          className={showArrowButtons ? 'with-arrows' : ''}
        />
      );
    }
    return coverPhotoDisplay;
  }, [showCarousel, carouselCards, coverPhotoDisplay, readOnly, showArrowButtons]);

  const showBadgeTag = Boolean(badges && showOverlayBadge && !showBadgeList);

  const filterCarouselClicks = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      if (!target) return;
      const arrowClicked = target.className.indexOf('slick-arrow') >= 0;
      const dotClicked = target.className.indexOf('carousel-dot') >= 0;
      const heartClicked = target.className.indexOf('favorite-icon') >= 0;
      if (!arrowClicked && !dotClicked && !heartClicked) {
        if (onClick) onClick(event);
        if (trackClick) doTrackClick(showBadgeTag);
      }
    },
    [doTrackClick, onClick, showBadgeTag, trackClick]
  );

  const showPostFavoriteOptIn = useShowPostFavoriteOptIn();

  const { isDesktop: initialDesktop } = useResponsiveDesign();
  const [tagSize, setTagSize] = useState<TagV2Size>(
    initialDesktop ? TagV2Size.LARGE : TagV2Size.SMALL
  );
  useEffectOnce(() => {
    if (isLargeDesktop() || isXsMobile()) {
      setTagSize(TagV2Size.LARGE);
    } else {
      setTagSize(TagV2Size.SMALL);
    }
  });

  return (
    <div
      className={cx(styles.cardContainer, className, { [styles.noMargin]: noMargin })}
      data-score={searchScore}
    >
      <CardLinkWrapper
        className={cx(styles.vendorCard, {
          [styles.readOnly]: readOnly,
        })}
        to={to}
        onClick={filterCarouselClicks}
        title={vendorName ?? undefined}
      >
        <div
          className={cx(styles.cardBody, {
            [styles.scaleOnHover]: scaleOnHover,
            [styles.sevenToFive]: aspectRatio === '7:5',
            [styles.altScaleOnHover]: altScaleOnHover,
          })}
        >
          {showBadgeTag && <ResponsiveVendorTagV2 size={tagSize} badges={badges} />}
          <div className={cx(styles.carouselContainer)}>
            {lazyload ? (
              <LazyLoad height={225} offset={300} once scrollContainer={scrollContainer}>
                {getCoverImage()}
              </LazyLoad>
            ) : (
              getCoverImage()
            )}
          </div>
          {storefrontUuid && showFavorites && !readOnly && (
            <StorefrontFavoriteButton
              className={styles.favoriteButton}
              uuid={storefrontUuid}
              tilePosition={position}
              location={location}
              whiteOutlineDefault
              onMouseEnter={() => setShowArrowButtons(true)}
              onMouseLeave={() => setShowArrowButtons(false)}
              vendorPhotoUuid={imageIds ? imageIds[0] : ''}
              afterFavoriteCallback={showPostFavoriteOptIn}
              suppressToast
              variant="v3"
            />
          )}
        </div>
        <div className={cx(styles.body, { [styles.bodyWithAward]: displayAward })}>
          <div
            className={cx(styles.vendorCardHeader, {
              [styles.inline]: showRatingStars && !(showVendorType || showLocation),
            })}
          >
            {(showVendorType || showLocation || showRatingStars) && (
              <VendorTypeAndLocation
                displayLocation={displayLocation}
                isUnderPricingRatingTest={isUnderPricingRatingTest}
                vendorTypeKey={taxonomyNodeKey}
                details={details}
                avgRating={vendor.averageReviewsRate}
                reviewsCount={reviewsCount}
                storefrontUuid={vendor.storefrontUuid}
                showVendorType={showVendorType}
                showRatingStars={showRatingStars}
              />
            )}
            <div className={styles.vendorName} data-testid="vendor-name">
              {vendorName}
            </div>
          </div>
          <div className={cx({ [styles.vendorCardDetailsContainer]: displayAward })}>
            <div className={styles.vendorCardDetails}>
              {isUnderPricingRatingTest && (
                <div className={styles.starRatingsLocation}>
                  {showRatingStars && vendor.averageReviewsRate && reviewsCount && (
                    <StarRatings
                      activeStars={formatRating(vendor.averageReviewsRate)}
                      fillColor={COLORS3.YELLOW_100}
                      reviewCount={reviewsCount.toString()}
                      textSize="small"
                      totalStars={5}
                    />
                  )}
                  {displayLocation && (
                    <span className={styles.location} data-test-id="location">
                      {formatLocation(displayLocation)}
                    </span>
                  )}
                </div>
              )}
              <CardDetails
                displayPrice={displayPrice}
                displayLocation={showServiceLocation}
                isPricingRatingTest1={isPricingRatingTest1}
                isPricingRatingTest2={isPricingRatingTest2}
                queriedLocation={queriedLocation}
                details={details}
                metroType={metroType}
                hasNoMinumumPrice={hasNoMinumumPrice}
                priceTier={priceTier}
                vendorTypeKey={taxonomyNodeKey}
                socialProof={vendor.socialProof}
                showDetails={showDetails}
                showHomeService={showHomeService}
              />
              {!!availabilityDates && !showBadgeList && (
                <AvailabilityTag
                  displayAvailability={displayAvailability}
                  vendorAvailableDates={vendorAvailableDates}
                />
              )}
              {showBadgeList && ((badges && badges?.length) || 0) > 0 && (
                <BadgeList badges={badges as VendorCardBadgeView[]} variant={badgeColorVariant} />
              )}
            </div>
            {displayAward ? (
              <div className={styles.bestOfZolaAward}>
                <img alt="Awarded Best of Zola" height={56} src={BestOfZolaPng} width={56} />
              </div>
            ) : null}
          </div>
        </div>
      </CardLinkWrapper>
    </div>
  );
};

export default VendorCard;
